/**
 * Onboarding Page
 * Guides new users through setting up their business profile
 */

import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, Building2, ArrowRight, CheckCircle } from 'lucide-react';
import { INDUSTRIES } from '@/pages/businessSettingsConstants';

export function OnboardingPage() {
  const navigate = useNavigate();

  // Check if user already has a business
  const currentBusiness = useQuery(api.businesses.queries.getCurrentBusiness);
  const currentUser = useQuery(api.users.queries.getCurrentUser);
  const createBusiness = useMutation(api.businesses.mutations.createBusiness);

  const [formData, setFormData] = useState({
    name: '',
    industry: '',
    customIndustry: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!formData.name.trim() || !formData.industry) {
        setError('Please fill in all required fields');
        return;
      }

      // Validate custom industry when "other" is selected
      if (formData.industry === 'other' && !formData.customIndustry.trim()) {
        setError('Please enter your industry');
        return;
      }

      if (!currentUser?._id) {
        setError('User not found. Please try signing in again.');
        return;
      }

      setIsSubmitting(true);
      setError(null);

      // Use custom industry if "other" is selected, otherwise use selected industry
      const industryValue =
        formData.industry === 'other' ? formData.customIndustry.trim() : formData.industry;

      try {
        await createBusiness({
          ownerId: currentUser._id,
          name: formData.name.trim(),
          industry: industryValue,
        });

        // Redirect to leads page after successful business creation
        navigate('/leads', { replace: true });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to create business');
        setIsSubmitting(false);
      }
    },
    [formData, currentUser, createBusiness, navigate]
  );

  const isValid =
    formData.name.trim() !== '' &&
    formData.industry !== '' &&
    (formData.industry !== 'other' || formData.customIndustry.trim() !== '');

  // Company users should never see onboarding — redirect to invitation accept or company portal
  const pendingInvitation = useQuery(
    api.companies.invitations.getMyPendingInvitation,
    currentUser?.role === 'company_user' ? {} : 'skip'
  );

  useEffect(() => {
    if (currentUser?.role === 'company_user') {
      if (pendingInvitation === undefined) return; // still loading
      if (pendingInvitation) {
        navigate(`/company-invite/${pendingInvitation.token}`, { replace: true });
      } else {
        navigate('/company/my-insurance', { replace: true });
      }
    }
  }, [currentUser, pendingInvitation, navigate]);

  // If user already has a business, redirect to leads
  useEffect(() => {
    if (currentBusiness !== undefined && currentBusiness !== null) {
      navigate('/leads', { replace: true });
    }
  }, [currentBusiness, navigate]);

  // Loading state while checking for existing business, or redirect in progress
  if (
    currentBusiness === undefined ||
    currentUser === undefined ||
    currentBusiness !== null ||
    currentUser?.role === 'company_user'
  ) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <div className="border-b border-border p-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <h1 className="text-xl font-semibold text-foreground">Wingrow</h1>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <CheckCircle className="h-4 w-4 text-green-500" />
            Account created
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-lg">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
              <Building2 className="h-8 w-8 text-primary" />
            </div>
            <h2 className="text-2xl font-bold text-foreground mb-2">Set Up Your Business</h2>
            <p className="text-muted-foreground">
              Tell us about your business to personalize your experience and AI-powered features.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="businessName">Business Name *</Label>
              <Input
                id="businessName"
                type="text"
                value={formData.name}
                onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., ABC Real Estate Agency"
                disabled={isSubmitting}
                className="bg-background-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="industry">Industry *</Label>
              <Select
                value={formData.industry}
                onValueChange={value =>
                  setFormData(prev => ({
                    ...prev,
                    industry: value,
                    // Clear custom industry when switching away from "other"
                    customIndustry: value === 'other' ? prev.customIndustry : '',
                  }))
                }
                disabled={isSubmitting}
              >
                <SelectTrigger className="bg-background-input">
                  <SelectValue placeholder="Select your industry" />
                </SelectTrigger>
                <SelectContent>
                  {INDUSTRIES.map(industry => (
                    <SelectItem key={industry.value} value={industry.value}>
                      {industry.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {formData.industry === 'other' && (
              <div className="space-y-2">
                <Label htmlFor="customIndustry">Your Industry *</Label>
                <Input
                  id="customIndustry"
                  type="text"
                  value={formData.customIndustry}
                  onChange={e => setFormData(prev => ({ ...prev, customIndustry: e.target.value }))}
                  placeholder="e.g., Photography, Event Planning"
                  disabled={isSubmitting}
                  className="bg-background-input"
                />
              </div>
            )}

            {error && (
              <div className="p-3 text-sm text-red-400 bg-red-900/20 border border-red-900/50 rounded-md">
                {error}
              </div>
            )}

            <Button type="submit" className="w-full" size="lg" disabled={!isValid || isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Creating business...
                </>
              ) : (
                <>
                  Continue
                  <ArrowRight className="h-4 w-4 ml-2" />
                </>
              )}
            </Button>
          </form>

          <p className="text-center text-sm text-muted-foreground mt-6">
            You can update these settings anytime in{' '}
            <button
              onClick={() => navigate('/business-settings')}
              className="text-primary hover:underline"
            >
              Business Settings
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
